// carousel.js — Beauty Charms lightweight carousel (no external libraries)
(function () {
  const ARROW_PREV_SVG = '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M15 5L8 12L15 19" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/></svg>';
  const ARROW_NEXT_SVG = '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M9 5L16 12L9 19" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/></svg>';

  class BCCarousel {
    constructor(el) {
      this.el = el;
      this.track = el.querySelector('[data-bc-carousel-track]');
      this.slides = Array.from(this.track ? this.track.children : []);
      if (!this.track || this.slides.length === 0) return;

      this.perViewDesktop = parseFloat(el.dataset.perViewDesktop || '4');
      this.perViewMobile = parseFloat(el.dataset.perViewMobile || '2');
      this.gap = parseInt(el.dataset.gap || '20', 10);
      this.infinite = el.dataset.infinite === 'true';
      this.autoplay = el.dataset.autoplay === 'true';
      this.speed = parseInt(el.dataset.speed || '4000', 10);
      this.showArrows = el.dataset.arrows !== 'false';
      this.showDots = el.dataset.dots === 'true';
      // Fraction of an extra card shown at the edge so the row visibly
      // reads as a *slider* rather than a static grid (fixes the "flat/
      // straight" look). Defaults to a gentle 12% peek.
      this.peek = el.dataset.peek !== undefined ? parseFloat(el.dataset.peek) / 100 : 0.12;

      this.index = 0;
      this.timer = null;

      this.build();
      this.bindEvents();
      this.observeResize();
      requestAnimationFrame(() => this.update());
    }

    perView() {
      return window.innerWidth <= 749 ? this.perViewMobile : this.perViewDesktop;
    }

    effectivePerView() {
      return this.perView() + this.peek;
    }

    applySlideWidths() {
      const n = this.effectivePerView();
      const basis = `calc(${100 / n}% - ${(this.gap * (n - 1)) / n}px)`;
      this.slides.forEach((slide) => {
        slide.style.flex = `0 0 ${basis}`;
      });
    }

    build() {
      this.el.classList.add('bc-carousel--peek');
      this.track.style.display = 'flex';
      this.track.style.gap = this.gap + 'px';
      this.track.style.willChange = 'transform';
      this.track.style.transition = 'transform 0.55s cubic-bezier(0.22,1,0.36,1)';
      this.applySlideWidths();

      if (this.showArrows) {
        this.prevBtn = document.createElement('button');
        this.prevBtn.type = 'button';
        this.prevBtn.className = 'bc-carousel__arrow bc-carousel__arrow--prev';
        this.prevBtn.setAttribute('aria-label', 'Previous');
        this.prevBtn.innerHTML = ARROW_PREV_SVG;
        this.nextBtn = document.createElement('button');
        this.nextBtn.type = 'button';
        this.nextBtn.className = 'bc-carousel__arrow bc-carousel__arrow--next';
        this.nextBtn.setAttribute('aria-label', 'Next');
        this.nextBtn.innerHTML = ARROW_NEXT_SVG;
        this.el.appendChild(this.prevBtn);
        this.el.appendChild(this.nextBtn);
      }

      if (this.showDots) {
        this.dotsWrap = document.createElement('div');
        this.dotsWrap.className = 'bc-carousel__dots';
        const dotCount = Math.max(1, Math.ceil(this.slides.length - this.perView()) + 1);
        for (let i = 0; i < dotCount; i++) {
          const dot = document.createElement('button');
          dot.type = 'button';
          dot.className = 'bc-carousel__dot';
          dot.setAttribute('aria-label', 'Go to slide ' + (i + 1));
          dot.addEventListener('click', () => this.goTo(i));
          this.dotsWrap.appendChild(dot);
        }
        this.el.appendChild(this.dotsWrap);
      }
    }

    bindEvents() {
      if (this.prevBtn) this.prevBtn.addEventListener('click', () => this.prev());
      if (this.nextBtn) this.nextBtn.addEventListener('click', () => this.next());

      let startX = 0;
      let dragging = false;
      this.track.addEventListener('touchstart', (e) => { startX = e.touches[0].clientX; dragging = true; }, { passive: true });
      this.track.addEventListener('touchend', (e) => {
        if (!dragging) return;
        const diff = startX - e.changedTouches[0].clientX;
        if (diff > 40) this.next();
        if (diff < -40) this.prev();
        dragging = false;
      }, { passive: true });

      let resizeTimer;
      window.addEventListener('resize', () => {
        clearTimeout(resizeTimer);
        resizeTimer = setTimeout(() => {
          this.applySlideWidths();
          this.index = Math.min(this.index, this.maxIndex());
          this.update(true);
        }, 120);
      });
    }

    observeResize() {
      const imgs = this.track.querySelectorAll('img');
      imgs.forEach((img) => {
        if (!img.complete) {
          img.addEventListener('load', () => this.update(true), { once: true });
        }
      });
    }

    stepSize() {
      return Math.max(1, Math.floor(this.perView()));
    }

    maxIndex() {
      return Math.max(0, Math.ceil(this.slides.length - this.perView()));
    }

    goTo(i) {
      this.index = Math.max(0, Math.min(i, this.maxIndex()));
      this.update();
    }

    next() {
      if (this.index >= this.maxIndex()) {
        this.index = this.infinite ? 0 : this.maxIndex();
      } else {
        this.index = Math.min(this.index + this.stepSize(), this.maxIndex());
      }
      this.update();
    }

    prev() {
      if (this.index <= 0) {
        this.index = this.infinite ? this.maxIndex() : 0;
      } else {
        this.index = Math.max(this.index - this.stepSize(), 0);
      }
      this.update();
    }

    update(instant) {
      if (instant) {
        this.track.style.transition = 'none';
        requestAnimationFrame(() => { this.track.style.transition = 'transform 0.55s cubic-bezier(0.22,1,0.36,1)'; });
      }
      const slideWidth = this.slides[0].getBoundingClientRect().width + this.gap;
      this.track.style.transform = `translateX(-${this.index * slideWidth}px)`;
      if (this.dotsWrap) {
        Array.from(this.dotsWrap.children).forEach((dot, i) => {
          dot.classList.toggle('is-active', i === this.index);
        });
      }
      if (this.prevBtn) this.prevBtn.disabled = !this.infinite && this.index === 0;
      if (this.nextBtn) this.nextBtn.disabled = !this.infinite && this.index >= this.maxIndex();
    }

    startAutoplay() {
      this.stopAutoplay();
      this.timer = setInterval(() => this.next(), this.speed);
    }

    stopAutoplay() {
      if (this.timer) clearInterval(this.timer);
    }
  }

  function initCarousels() {
    document.querySelectorAll('[data-bc-carousel]').forEach((el) => {
      if (!el.dataset.bcCarouselInit) {
        el.dataset.bcCarouselInit = 'true';
        const instance = new BCCarousel(el);
        if (instance.autoplay) {
          instance.startAutoplay();
          el.addEventListener('mouseenter', () => instance.stopAutoplay());
          el.addEventListener('mouseleave', () => instance.startAutoplay());
        }
      }
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initCarousels);
  } else {
    initCarousels();
  }
})();